/**
 * One flight world: the flight screens read the flight record and the
 * allocation plan, not a document of their own.
 *
 * The screens' domain (flights, stands, turnarounds, FIDS, delays, VDGS) was
 * written against `Flight[]` and a `StandRegister`. This adapter builds both
 * from the record (AODB movements) and the allocation day, and turns a whole
 * register the stands screen saves back into row-level changes — so the
 * domain stays as it is and there is one truth underneath.
 *
 * ── what it keeps straight ──────────────────────────────────────────────
 *
 *  - Times are normalised to `toISOString()`: the screens compare ISO strings
 *    as strings, and the platform sends `+00:00` and microseconds.
 *  - A turnaround key is the arrival's id, so exactly the two linked
 *    movements share it, and never across days.
 *  - Size comes only from the allocation's demand, and only for a movement
 *    with a type: a missing size means "don't check", and inventing "C"
 *    would invent a fit.
 *  - A stand is booked per visit, keyed by the arrival; the departure's stand
 *    is found through the demand's movement ids. Plan segment times are
 *    stale — the demand's times are the record's current ones.
 *  - The screens' domain adds a delay to a stay itself, so `onPlannedTimes`
 *    takes it back out of the demand's times before the screens see them.
 *  - Work-order closures are left to `blocksFromWorkOrders`, which the
 *    screens already run; only the airport's own closures put a stand out of
 *    service here.
 *
 * Pure. No React, no fetch, and `now` is always passed in.
 */
import { t } from "../i18n";
import { AERODROME_ZONE } from "../lib/format";
import type { Movement } from "./aodb";
import type { AllocDay, Assignment, Demand, Resource } from "./allocation";
import { shiftedWindow, SOURCE_LABEL, type DelayNotice, type DelaySource } from "./flightDelays";
import { SERVICE_LABEL, type Flight, type FlightSource, type ServiceType } from "./flights";
import type { Stand, StandAssignment, StandKind, StandRegister } from "./stands";

/** An instant as the screens compare it, or undefined when it is not one. */
function iso(x: string | null | undefined): string | undefined {
  if (!x) return undefined;
  const ms = Date.parse(x);
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

const PAX_USES = ["pax", "cargo", "ferry", "positioning"];

/* ── the record as flights ────────────────────────────────────────────── */

/** Arrival id → departure id, one to one, as the engine pairs them. */
function pairsOf(movements: readonly Movement[]): Map<string, string> {
  const byId = new Map(movements.map((m) => [m.id, m]));
  const arrToDep = new Map<string, string>();
  const paired = new Set<string>();
  const link = (arr: Movement | undefined, dep: Movement | undefined) => {
    if (!arr || !dep || arr.direction !== "arrival" || dep.direction !== "departure") return;
    if (paired.has(arr.id) || paired.has(dep.id)) return;
    arrToDep.set(arr.id, dep.id);
    paired.add(arr.id);
    paired.add(dep.id);
  };
  /* a departure names its inbound, or an arrival names its outbound */
  for (const m of movements) if (m.direction === "departure" && m.linkedMovementId) link(byId.get(m.linkedMovementId), m);
  for (const m of movements) if (m.direction === "arrival" && m.linkedMovementId) link(m, byId.get(m.linkedMovementId));
  return arrToDep;
}

function sourceOf(m: Movement): FlightSource {
  if (m.source === "feed") return "feed";
  if (m.source === "manual") return "manual";
  return "schedule";
}

function actualOf(m: Movement): string | undefined {
  const ms = m.milestones ?? {};
  return (
    iso(m.actualAt) ??
    (m.direction === "arrival" ? iso(ms.arrived) ?? iso(ms.reached_position) : iso(ms.departed) ?? iso(ms.left_position))
  );
}

function actualMark(m: Movement): { source: string; by?: string } | undefined {
  const p = m.provenance ?? {};
  const keys =
    m.direction === "arrival"
      ? ["actual_at", "milestones.reached_position", "milestones.arrived"]
      : ["actual_at", "milestones.left_position", "milestones.departed"];
  for (const k of keys) if (p[k]) return p[k];
  return undefined;
}

function noticesOf(m: Movement): DelayNotice[] {
  const out: DelayNotice[] = [];
  for (const n of m.notices ?? []) {
    const newTime = iso(n.newTime);
    if (!newTime) continue;
    out.push({
      at: iso(n.at) ?? n.at,
      source: n.source in SOURCE_LABEL ? (n.source as DelaySource) : "airport_ops",
      ...(n.by ? { by: n.by } : {}),
      newTime,
      ...(n.code ? { code: n.code } : {}),
      ...(n.note ? { note: n.note } : {}),
    });
  }
  return out;
}

/**
 * The record's movements as the flight screens' flights. Pass the
 * allocation day for sizes and gates; without it neither is filled in.
 */
export function flightsFromRecord(movements: readonly Movement[], day?: AllocDay | null): Flight[] {
  const arrToDep = pairsOf(movements);
  const depToArr = new Map([...arrToDep].map(([a, d]) => [d, a]));
  const position = new Map<string, Demand>();
  const anyDemand = new Map<string, Demand>();
  for (const d of day?.demands ?? [])
    for (const id of d.movementIds) {
      if (d.kind === "position" && !position.has(id)) position.set(id, d);
      if (!anyDemand.has(id)) anyDemand.set(id, d);
    }
  const plan = new Map((day?.plan ?? []).map((a) => [a.key, a]));

  return movements.map((m) => {
    const actualAt = actualOf(m);
    const cancelled = m.status === "cancelled" || Boolean(m.cancelledReason);
    const turnaround = arrToDep.has(m.id) ? m.id : depToArr.get(m.id);
    const demand = position.get(m.id) ?? anyDemand.get(m.id);
    const gate = m.direction === "departure" && !cancelled ? plan.get(`gate:${m.id}`)?.segments[0]?.codes[0] : undefined;
    const notices = noticesOf(m);
    const passengers = m.loads?.actual ?? m.loads?.booked;
    const f: Flight = {
      id: m.id,
      direction: m.direction,
      carrier: m.carrier || undefined,
      number: m.number || undefined,
      registration: m.registration || undefined,
      aircraftIcao: m.classKey || undefined,
      otherAirport: m.counterpartNode || undefined,
      scheduledAt: iso(m.scheduledAt),
      estimatedAt: actualAt ? undefined : iso(m.estimatedAt),
      actualAt,
      source: sourceOf(m),
    };
    if (m.serviceType && m.serviceType in SERVICE_LABEL) f.serviceType = m.serviceType as ServiceType;
    if (turnaround) f.turnaround = turnaround;
    if (m.classKey && demand) f.size = demand.size;
    if (m.delayCodes?.[0]?.code) f.delayCode = m.delayCodes[0].code;
    if (m.remarks) f.delayNote = m.remarks;
    if (m.capacity != null) f.seats = m.capacity;
    if (passengers != null) f.passengers = passengers;
    if (notices.length) f.notices = notices;
    if (cancelled) f.cancelled = { at: "", by: "", reason: m.cancelledReason ?? t("Cancelled") };
    if (gate) f.gate = gate;
    /* where the actual came from: the record's own mark on the time, else on the milestone it was read from */
    const said = actualAt ? actualMark(m) : undefined;
    if (said) {
      f.actualSource = said.source;
      if (said.by) f.actualBy = said.by;
    }
    const tobt = m.direction === "departure" ? iso(m.milestones?.tobt) : undefined;
    if (tobt) {
      f.tobt = tobt;
      const by = m.provenance?.["milestones.tobt"]?.by;
      if (by) f.tobtBy = by;
    }
    return f;
  });
}

/* ── the plan as a stand register ─────────────────────────────────────── */

/** The order the stand screens list aprons in; anything else follows. */
const APRON_ORDER = ["APRON-1", "APRON-R", "APRON-2", "APRON-C", "APRON-G", "APRON-M"];

function kindOf(r: Resource): StandKind {
  const only = r.uses?.length === 1 ? r.uses[0] : undefined;
  if (only === "cargo") return "cargo";
  if (only === "ga") return "ga";
  if (only === "maintenance") return "maintenance";
  return r.servedInPlace ? "contact" : "remote";
}

/** A closure ref from a work order: those are the screens' to show, with the order. */
const fromWorkOrder = (ref: string | undefined) => Boolean(ref && /^WO-/i.test(ref));

function standOf(r: Resource, now: number): Stand {
  const s: Stand = {
    code: r.code,
    kind: kindOf(r),
    /* no limit on the register means any size */
    maxSize: r.maxSize ?? "F",
    bridge: Boolean(r.servedInPlace),
  };
  if (r.name) s.name = r.name;
  if (r.group) s.zoneCode = r.group;
  if (r.excludes?.length) s.excludes = [...r.excludes];
  if (r.links?.[0]) s.gate = r.links[0];
  const shut = (r.closed ?? []).find(
    (c) =>
      !fromWorkOrder(c.ref) &&
      (!c.from || !(Date.parse(c.from) > now)) &&
      (!c.to || !(Date.parse(c.to) <= now)),
  );
  if (shut)
    s.outOfService = {
      ...(shut.from ? { from: shut.from } : {}),
      ...(shut.to ? { to: shut.to } : {}),
      why: shut.why,
      ...(shut.ref ? { by: shut.ref } : {}),
    };
  return s;
}

const groupRank = (g: string | undefined) => {
  const i = g ? APRON_ORDER.indexOf(g) : -1;
  return i >= 0 ? i : APRON_ORDER.length;
};

function byApronThenCode(a: Stand, b: Stand): number {
  const ra = groupRank(a.zoneCode);
  const rb = groupRank(b.zoneCode);
  if (ra !== rb) return ra - rb;
  if (ra === APRON_ORDER.length && a.zoneCode !== b.zoneCode) {
    /* other aprons by name; a stand with no apron last */
    if (!a.zoneCode) return 1;
    if (!b.zoneCode) return -1;
    return a.zoneCode.localeCompare(b.zoneCode, "en", { numeric: true });
  }
  return a.code.localeCompare(b.code, "en", { numeric: true });
}

function placedBy(source: Assignment["source"]): string {
  switch (source) {
    case "person":
      return t("Placed by hand");
    case "engine":
      return t("Allocation engine");
    case "auto":
      return t("Automatic");
    default:
      return t("Stand plan");
  }
}

/**
 * The allocation day as the stand screens' register: the stands from the
 * position resources, and one assignment per placed visit, at the record's
 * current times.
 */
export function registerFromPlan(day: AllocDay, now: Date): StandRegister {
  const at = now.getTime();
  const stands = day.resources
    .filter((r) => r.kind === "position")
    .map((r) => standOf(r, at))
    .sort(byApronThenCode);
  const demands = new Map(day.demands.map((d) => [d.key, d]));
  const assignments: StandAssignment[] = [];
  for (const a of day.plan) {
    if (a.kind !== "position") continue;
    /* a plan row with no demand is outside the horizon or orphaned by a re-pairing */
    const d = demands.get(a.key);
    const code = a.segments[0]?.codes[0];
    if (!d || !code || !d.movementIds[0]) continue;
    const from = iso(d.from);
    const to = iso(d.to);
    if (!from || !to) continue;
    assignments.push({
      id: a.key,
      standCode: code,
      flightId: d.movementIds[0],
      from,
      to,
      actual: d.basisStart === "actual" && Date.parse(from) <= at,
      by: placedBy(a.source),
      ...(a.tow ? { note: t("towed") } : {}),
    });
  }
  return { stands, assignments };
}

/**
 * The register on the plan's own footing: each stay as it was planned, with
 * the delays taken back out.
 *
 * The allocation's demand times are the record's latest — a late arrival's
 * stay already starts late. The screens' domain keeps the plan and the delay
 * apart: a stay is the plan, and `shiftedWindow` adds what the flights now
 * say. Fed the latest times, it would count every delay twice, and a delay
 * running into the next flight would show only as a clash "already in the
 * plan" — the knock-on the delays and stands screens exist to show would
 * never appear. So the lateness `shiftedWindow` will add is taken off here;
 * shifted again, each stay is back at the record's time.
 *
 * Only the times move. Keys, stands and flights are as the plan has them, so
 * a saved register still diffs to the same writes.
 */
export function onPlannedTimes(register: StandRegister, flights: readonly Flight[]): StandRegister {
  return {
    ...register,
    assignments: register.assignments.map((a) => {
      const w = shiftedWindow(a, flights);
      const from = Date.parse(a.from);
      const to = Date.parse(a.to);
      const early = w.from - from;
      if (early === 0 && w.shift === 0) return a;
      const planFrom = from - early;
      /* a stay never ends before it starts, however the delays fell */
      const planTo = Math.max(to - w.shift * 60_000, planFrom + 60_000);
      return { ...a, from: new Date(planFrom).toISOString(), to: new Date(planTo).toISOString() };
    }),
  };
}

/* ── a saved register as row-level changes ────────────────────────────── */

export type Op =
  | { kind: "assign"; key: string; code: string; flightLabel: string }
  | { kind: "release"; key: string }
  | { kind: "add"; resource: Resource }
  /** a placement the plan has no demand for (outside its horizon): not sent, reported */
  | { kind: "skip"; flightId: string };

/** The stand screens' stand as an allocation resource — the inverse of `registerFromPlan`. */
export function resourceFromStand(s: Stand): Resource {
  const uses =
    s.kind === "cargo" ? ["cargo"] : s.kind === "ga" ? ["ga"] : s.kind === "maintenance" ? ["maintenance"] : [...PAX_USES];
  const r: Resource = {
    kind: "position",
    code: s.code,
    maxSize: s.maxSize,
    servedInPlace: Boolean(s.bridge) || s.kind === "contact",
    uses,
  };
  if (s.name) r.name = s.name;
  if (s.zoneCode) r.group = s.zoneCode;
  if (s.excludes?.length) r.excludes = [...s.excludes];
  if (s.gate) r.links = [s.gate];
  return r;
}

/**
 * What changed between the register the screen showed and the one it saved,
 * as the writes that make it so: stands added first (so a flight can be put
 * on one), then releases, then placements.
 *
 * An assignment is matched to its plan row by key: its id when it came from
 * the plan, else the position demand that carries its flight. Diffing by key
 * rather than by id makes an Undo — which saves a whole older register —
 * come out as just the moves that differ.
 */
export function registerChanges(before: StandRegister, after: StandRegister, day: AllocDay): Op[] {
  const positions = day.demands.filter((d) => d.kind === "position");
  const byKey = new Map(positions.map((d) => [d.key, d]));
  const skipped: Op[] = [];
  const keyOf = (a: StandAssignment, report: boolean): string | undefined => {
    if (a.id.startsWith("position:")) return a.id;
    const d = positions.find((x) => x.movementIds.includes(a.flightId));
    if (!d && report) skipped.push({ kind: "skip", flightId: a.flightId });
    return d?.key;
  };
  const codes = (r: StandRegister, report: boolean) => {
    const out = new Map<string, string>();
    for (const a of r.assignments) {
      const k = keyOf(a, report);
      if (k && !out.has(k)) out.set(k, a.standCode);
    }
    return out;
  };
  const was = codes(before, false);
  /* only a placement that is new or moved needs a key; an untouched one is not reported */
  const unchanged = new Set(before.assignments.map((a) => `${a.id}|${a.standCode}|${a.flightId}`));
  const now = new Map<string, string>();
  for (const a of after.assignments) {
    const k = keyOf(a, !unchanged.has(`${a.id}|${a.standCode}|${a.flightId}`));
    if (k && !now.has(k)) now.set(k, a.standCode);
  }

  const ops: Op[] = [];
  const known = new Set(before.stands.map((s) => s.code));
  for (const s of after.stands) if (!known.has(s.code)) ops.push({ kind: "add", resource: resourceFromStand(s) });
  for (const k of was.keys()) if (!now.has(k)) ops.push({ kind: "release", key: k });
  for (const [k, code] of now)
    if (was.get(k) !== code) ops.push({ kind: "assign", key: k, code, flightLabel: byKey.get(k)?.label ?? k });
  return [...ops, ...skipped];
}

/* ── the operating day ────────────────────────────────────────────────── */

const dateFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: AERODROME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today's service date at the aerodrome, YYYY-MM-DD. */
export function serviceDateToday(now: Date): string {
  const parts = Object.fromEntries(dateFmt.formatToParts(now).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** A service date `days` away from `date` (YYYY-MM-DD), by the calendar. */
export function shiftServiceDate(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** How far back a flight from another day still belongs on the boards, and how far ahead. */
const WINDOW_BACK_MS = 3 * 3600_000;
const WINDOW_AHEAD_MS = 18 * 3600_000;
/** An unflown flight from yesterday stays this long past its last known time. */
const OPEN_BACK_MS = 12 * 3600_000;

/**
 * Whether a movement belongs on the operating screens now. All of today's
 * service date does. From the days either side: what falls in the
 * allocation's own horizon (3 hours back, 18 ahead), so the boards look past
 * midnight, and yesterday's flights that have not flown and are not
 * cancelled, so a late one does not drop off the screens at midnight.
 */
export function inOperatingWindow(m: Movement, today: string, now: Date): boolean {
  if (m.serviceDate === today) return true;
  const at = Date.parse(m.actualAt ?? m.estimatedAt ?? m.scheduledAt ?? "");
  if (Number.isNaN(at)) return false;
  const t0 = now.getTime();
  if (at >= t0 - WINDOW_BACK_MS && at <= t0 + WINDOW_AHEAD_MS) return true;
  const open = !actualOf(m) && m.status !== "cancelled" && !m.cancelledReason && m.status !== "diverted";
  return m.serviceDate < today && open && at >= t0 - OPEN_BACK_MS;
}
